import { Menu, MenuItem, Stack, Typography } from '@mui/material'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Icons } from 'utoniq-ui'

import {
  Maybe,
  TradeBoxFragment,
  TradeCandidateNegotiationStatus,
  TradeMyCandidatesConnectionFragment,
  TradeNegotiationsMyRequestsConnectionFragment,
  TradeNegotiationStatus,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { TradeBox, TradeBoxSkeleton } from '@/components/organisms'
import { ScrollFetcher, Tabs } from '@/components/organisms/functionals'
import { User as AuthUser, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  hasNextPageRequest: boolean
  hasNextPageCandidates: boolean
  currentUser: Maybe<AuthUser>
  tradeRequests: Maybe<TradeNegotiationsMyRequestsConnectionFragment>
  tradeCandidates: Maybe<TradeMyCandidatesConnectionFragment>
  filter: {
    type: string
    eventFilter: string
  }
}

type TransactionCellProps = {
  loading: State['loading']
  trades: State['tradeRequests'] | State['tradeCandidates']
  hasNextPage: State['hasNextPageRequest'] | State['hasNextPageCandidates']
  onFetchMore: Action['onFetchMore']
  onChangeStatus: Action['onChangeStatus']
  type: 'request' | 'candidate'
  myUserId: string
  filter: {
    type: string
    eventFilter: string
  }
}

const TRADE_STATUS = {
  request: [
    [TradeNegotiationStatus.InProgress],
    [TradeNegotiationStatus.Agreed],
    [TradeNegotiationStatus.BrokenOff, TradeNegotiationStatus.Dropped],
  ],
  candidate: [
    [TradeCandidateNegotiationStatus.InProgress],
    [TradeCandidateNegotiationStatus.Accepted],
    [
      TradeCandidateNegotiationStatus.Rejected,
      TradeCandidateNegotiationStatus.Dropped,
      TradeCandidateNegotiationStatus.Expired,
    ],
  ],
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: (values: {
    status?: Array<TradeNegotiationStatus | TradeCandidateNegotiationStatus>
    type: 'request' | 'candidate'
  }) => Promise<void>
  onChangeStatus: (values: {
    status?: Array<TradeNegotiationStatus | TradeCandidateNegotiationStatus>
    type: 'request' | 'candidate'
  }) => Promise<void>
  onChangeSegment: (values: { type: 'request' | 'candidate' }) => Promise<void>
}

const tabs: Array<'request' | 'candidate'> = ['candidate', 'request']

export const MyTradeTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onChangeStatus,
  onChangeSegment,
}) => {
  const { routerNames } = useRouter()
  const { t } = useTranslation('myTrade')
  const [value, setValue] = useState<number>(
    state.filter.type === 'request' ? 1 : 0
  )
  const [filter, setFilter] = useState<{ type: string; eventFilter: string }>({
    type: state.filter.type,
    eventFilter: state.filter.eventFilter,
  })

  const handleChangeSegment = useCallback(
    async (newValue: number) => {
      setValue(newValue)
      await onChangeSegment({ type: newValue ? 'request' : 'candidate' })
    },
    [onChangeSegment]
  )

  useEffect(() => {
    setFilter({
      type: value ? 'request' : 'candidate',
      eventFilter: 'all',
    })
  }, [value])

  return (
    <BaseLayout header={{ back: true, title: t('title') }}>
      <Head title={routerNames.MyTrade} />
      <Tabs
        value={value}
        onChange={handleChangeSegment}
        disableAnimateTransitions
        labels={[t('tradesFiled'), t('tradeApplied')]}
      >
        {tabs.map((type, index) => (
          <TransactionCell
            key={index}
            type={type}
            loading={state.loading}
            hasNextPage={
              type === 'request'
                ? state.hasNextPageRequest
                : state.hasNextPageCandidates
            }
            trades={
              type === 'request' ? state.tradeRequests : state.tradeCandidates
            }
            myUserId={state.currentUser?.uid ?? ''}
            onFetchMore={onFetchMore}
            onChangeStatus={onChangeStatus}
            filter={filter}
          />
        ))}
      </Tabs>
    </BaseLayout>
  )
}

const TransactionCell: Cell<TransactionCellProps> = ({
  loading,
  hasNextPage,
  trades,
  onFetchMore,
  onChangeStatus,
  type,
  myUserId,
  filter,
}) => {
  const { t } = useTranslation('myTrade')
  const { routerPages, generatePath } = useRouter()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const open = Boolean(anchorEl)

  const options = useMemo(
    () => [
      { param: 'all', label: t('all'), value: undefined },
      { param: 'duringTrading', label: t('duringTrading'), value: 0 },
      { param: 'success', label: t('success'), value: 1 },
      { param: 'failure', label: t('failure'), value: 2 },
    ],
    [t]
  )

  const initialSelectedValue =
    options.findIndex(option => option.param === filter.eventFilter) ?? 0

  const [selectedIndex, setSelectedIndex] = useState(
    initialSelectedValue === -1 ? 0 : initialSelectedValue
  )
  useEffect(() => {
    const UrlParams = `?type=${type}&filter=${options[selectedIndex].param}`
    // React Routerのhistoryを使うとソート順を変えた際に、スクロール位置が一番上になるため、JavaScriptで直接historyを利用している
    window.history.replaceState(null, '', UrlParams)
  }, [selectedIndex])

  useEffect(() => {
    onChangeStatus({ status: TRADE_STATUS[type][selectedIndex - 1], type })
  }, [])

  const handleFilterClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuItemClick = useCallback(
    (event: MouseEvent<HTMLElement>, newIndex: number) => {
      setSelectedIndex(newIndex)
      setAnchorEl(null)
      return onChangeStatus({ status: TRADE_STATUS[type][newIndex - 1], type })
    },
    [onChangeStatus, type]
  )

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleFetchMore = useCallback(async () => {
    await onFetchMore({ status: TRADE_STATUS[type][selectedIndex - 1], type })
  }, [onFetchMore, selectedIndex, type])

  return (
    <CellFrame sx={{ px: 5 }}>
      <Stack direction="row-reverse">
        <Button
          variant="text"
          sx={{ width: 'fit-content', mr: -2 }}
          onClick={handleFilterClick}
        >
          <Typography variant="caption" sx={{ pr: 1 }}>
            {options[selectedIndex].label}
          </Typography>
          <Icons.DownArrow fontSize="xSmall" sx={{ color: 'grey.600' }} />
        </Button>
        <Menu
          id="lock-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'lock-button',
            role: 'listbox',
          }}
        >
          {options.map((option, index) => (
            <MenuItem
              sx={{ bgcolor: 'grey.50', minWidth: 200 }}
              key={index}
              selected={index === selectedIndex}
              onClick={event => handleMenuItemClick(event, index)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
      <ScrollFetcher
        loading={loading}
        hasNextPage={hasNextPage}
        componentProps={{ spacing: 4 }}
        onFetchMore={handleFetchMore}
        emptyMessage={t('tradeEmptyMessage')}
        nodes={trades?.edges?.map(({ node }, index) => {
          const trades = isTradeNegotiation(node) ? node.tradeNegotiation : node
          if (!trades) return <></>

          return (
            <TradeBox
              fullWidth
              key={index}
              myUserId={myUserId}
              trades={trades}
              href={generatePath(routerPages.MyTradeDetail, {
                tradeNegotiationId: isTradeNegotiation(node)
                  ? node.tradeNegotiation.id
                  : node.id,
              })}
            />
          )
        })}
        loadingComponent={
          <Stack spacing={6} sx={{ py: 2 }}>
            {Array.from(Array(3).keys()).map(key => (
              <TradeBoxSkeleton fullWidth key={key} />
            ))}
          </Stack>
        }
      />
    </CellFrame>
  )
}

function isTradeNegotiation(
  data: TradeBoxFragment | { tradeNegotiation: TradeBoxFragment }
): data is { tradeNegotiation: TradeBoxFragment } {
  return 'tradeNegotiation' in data
}
